import { defineMessages, type MessageKey } from './define';

/**
 * Textos comunes (navegación, acciones, estados, unidades, aria-labels, errores de red).
 * Los textos propios de cada componente viven junto a él (`*Msgs.ts`). NO cambies ni borres las
 * claves `errors.*` ni `actions.*` ya publicadas: las usa la capa HTTP y otras features.
 */
export const commonMsgs = defineMessages(
  'common',
  {
    appName: 'Pizarra Agro',
    tagline: 'Los precios del campo, al dólar BCV',
    actions: {
      retry: 'Reintentar',
      close: 'Cerrar',
      cancel: 'Cancelar',
      save: 'Guardar',
      delete: 'Eliminar',
      accept: 'Aceptar',
      confirm: 'Confirmar',
      back: 'Volver',
      next: 'Siguiente',
      edit: 'Editar',
      add: 'Añadir',
      remove: 'Quitar',
      clear: 'Limpiar',
      apply: 'Aplicar',
      reset: 'Restablecer',
      share: 'Compartir',
      copy: 'Copiar',
      download: 'Descargar',
      export: 'Exportar',
      search: 'Buscar',
      filter: 'Filtrar',
      seeMore: 'Ver más',
      seeLess: 'Ver menos',
      undo: 'Deshacer',
      install: 'Instalar',
      update: 'Actualizar',
      signIn: 'Entrar',
      signOut: 'Salir',
      signUp: 'Crear cuenta',
      open: 'Abrir',
      goHome: 'Ir al inicio',
      reload: 'Recargar la página',
    },
    nav: {
      skipToContent: 'Saltar al contenido',
      main: 'Navegación principal',
      bottom: 'Navegación inferior',
      more: 'Más',
      moreTitle: 'Más secciones',
      inicio: 'Inicio',
      precios: 'Precios',
      historico: 'Histórico',
      comparador: 'Comparador',
      tendencias: 'Tendencias',
      noticias: 'Noticias',
      calculadora: 'Calculadora',
      alertas: 'Alertas',
      notificaciones: 'Notificaciones',
      cuenta: 'Mi cuenta',
      entrar: 'Entrar',
      registro: 'Crear cuenta',
      recuperar: 'Recuperar contraseña',
      restablecer: 'Nueva contraseña',
      verificar: 'Verificar correo',
      fuentes: 'Fuentes',
      admin: 'Administración',
      ajustes: 'Ajustes',
      acerca: 'Acerca de',
      groupMercado: 'Mercado',
      groupHerramientas: 'Herramientas',
      groupInformacion: 'Información',
      groupCuenta: 'Cuenta',
    },
    states: {
      loading: 'Cargando…',
      loadingPage: 'Cargando la pantalla…',
      empty: 'No hay nada por aquí',
      noData: 'Sin dato',
      comingSoonTitle: 'En construcción',
      comingSoonText: 'Estamos armando esta pantalla. Vuelve pronto.',
      noPermissionTitle: 'Sin permiso',
      noPermissionText: 'Tu cuenta no tiene acceso a esta sección.',
      notFoundTitle: 'No encontramos esa página',
      notFoundText: 'La dirección no existe o cambió de lugar. Vuelve al inicio y sigue desde ahí.',
      crashTitle: 'Algo se rompió por acá',
      crashText:
        'La pantalla falló de forma inesperada. Tus datos locales están a salvo; reintenta o recarga.',
      offlineShort: 'Sin conexión',
      online: 'En línea',
      apiDown: 'Sin respuesta del servidor',
    },
    units: {
      kg: 'kg',
      liter: 'L',
      ton: 't',
      unit: 'unidad',
      head: 'cabeza|cabezas',
      usd: 'US$',
      bs: 'Bs.',
    },
    aria: {
      brandHome: 'Pizarra Agro, ir al inicio',
      notifications: 'Notificaciones',
      notificationsUnread: 'Notificaciones, {count} sin leer',
      account: 'Cuenta',
      accountMenu: 'Menú de la cuenta',
      theme: 'Cambiar tema',
      themeLight: 'Claro',
      themeDark: 'Oscuro',
      themeSystem: 'Según el sistema',
      connection: 'Estado de la conexión',
      bcvLink: 'Tasa BCV: {tasa} bolívares por dólar. Ir al inicio',
      bcvUnit: 'bolívares por dólar',
      pageLoaded: 'Pantalla cargada: {title}',
      required: 'obligatorio',
      loading: 'Cargando',
    },
    errors: {
      networkTitle: 'Sin conexión',
      networkDetail:
        'No se pudo contactar con el servidor. Revisa tu conexión e inténtalo de nuevo.',
      unauthorizedTitle: 'Sesión no válida',
      unauthorizedDetail: 'Tu sesión venció. Inicia sesión de nuevo.',
      forbiddenTitle: 'Sin permiso',
      forbiddenDetail: 'No tienes permiso para hacer esto.',
      notFoundTitle: 'No encontrado',
      notFoundDetail: 'No encontramos lo que buscas.',
      conflictTitle: 'Conflicto',
      conflictDetail: 'Los datos cambiaron en otro lugar. Recarga e inténtalo de nuevo.',
      validationTitle: 'Datos no válidos',
      validationDetail: 'Revisa los campos marcados.',
      rateLimitTitle: 'Demasiados intentos',
      rateLimitDetail: 'Espera un momento antes de volver a intentarlo.',
      serverTitle: 'Error del servidor',
      serverDetail: 'El servidor tuvo un problema. Inténtalo de nuevo en unos minutos.',
      genericTitle: 'Algo salió mal',
      genericDetail: 'No pudimos completar la acción.',
      badResponseTitle: 'Respuesta inesperada',
      badResponseDetail: 'El servidor respondió con un formato que no reconocemos.',
    },
  },
  {
    appName: 'Pizarra Agro',
    tagline: 'Farm prices, in BCV dollars',
    actions: {
      retry: 'Retry',
      close: 'Close',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      accept: 'OK',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      edit: 'Edit',
      add: 'Add',
      remove: 'Remove',
      clear: 'Clear',
      apply: 'Apply',
      reset: 'Reset',
      share: 'Share',
      copy: 'Copy',
      download: 'Download',
      export: 'Export',
      search: 'Search',
      filter: 'Filter',
      seeMore: 'See more',
      seeLess: 'See less',
      undo: 'Undo',
      install: 'Install',
      update: 'Update',
      signIn: 'Sign in',
      signOut: 'Sign out',
      signUp: 'Create account',
      open: 'Open',
      goHome: 'Go home',
      reload: 'Reload the page',
    },
    nav: {
      skipToContent: 'Skip to content',
      main: 'Main navigation',
      bottom: 'Bottom navigation',
      more: 'More',
      moreTitle: 'More sections',
      inicio: 'Home',
      precios: 'Prices',
      historico: 'History',
      comparador: 'Compare',
      tendencias: 'Trends',
      noticias: 'News',
      calculadora: 'Calculator',
      alertas: 'Alerts',
      notificaciones: 'Notifications',
      cuenta: 'My account',
      entrar: 'Sign in',
      registro: 'Create account',
      recuperar: 'Recover password',
      restablecer: 'New password',
      verificar: 'Verify email',
      fuentes: 'Sources',
      admin: 'Administration',
      ajustes: 'Settings',
      acerca: 'About',
      groupMercado: 'Market',
      groupHerramientas: 'Tools',
      groupInformacion: 'Information',
      groupCuenta: 'Account',
    },
    states: {
      loading: 'Loading…',
      loadingPage: 'Loading the screen…',
      empty: 'Nothing here yet',
      noData: 'No data',
      comingSoonTitle: 'Under construction',
      comingSoonText: 'We are building this screen. Check back soon.',
      noPermissionTitle: 'Not allowed',
      noPermissionText: 'Your account cannot access this section.',
      notFoundTitle: 'We could not find that page',
      notFoundText:
        'The address does not exist or has moved. Go back home and continue from there.',
      crashTitle: 'Something broke here',
      crashText: 'The screen failed unexpectedly. Your local data is safe; retry or reload.',
      offlineShort: 'Offline',
      online: 'Online',
      apiDown: 'No answer from the server',
    },
    units: {
      kg: 'kg',
      liter: 'L',
      ton: 't',
      unit: 'unit',
      head: 'head|heads',
      usd: 'US$',
      bs: 'Bs.',
    },
    aria: {
      brandHome: 'Pizarra Agro, go to home',
      notifications: 'Notifications',
      notificationsUnread: 'Notifications, {count} unread',
      account: 'Account',
      accountMenu: 'Account menu',
      theme: 'Change theme',
      themeLight: 'Light',
      themeDark: 'Dark',
      themeSystem: 'Follow system',
      connection: 'Connection status',
      bcvLink: 'BCV rate: {tasa} bolivars per dollar. Go to home',
      bcvUnit: 'bolivars per dollar',
      pageLoaded: 'Screen loaded: {title}',
      required: 'required',
      loading: 'Loading',
    },
    errors: {
      networkTitle: 'No connection',
      networkDetail: 'Could not reach the server. Check your connection and try again.',
      unauthorizedTitle: 'Invalid session',
      unauthorizedDetail: 'Your session expired. Sign in again.',
      forbiddenTitle: 'Not allowed',
      forbiddenDetail: 'You do not have permission to do this.',
      notFoundTitle: 'Not found',
      notFoundDetail: 'We could not find what you are looking for.',
      conflictTitle: 'Conflict',
      conflictDetail: 'The data changed elsewhere. Reload and try again.',
      validationTitle: 'Invalid data',
      validationDetail: 'Check the highlighted fields.',
      rateLimitTitle: 'Too many attempts',
      rateLimitDetail: 'Wait a moment before trying again.',
      serverTitle: 'Server error',
      serverDetail: 'The server had a problem. Try again in a few minutes.',
      genericTitle: 'Something went wrong',
      genericDetail: 'We could not complete the action.',
      badResponseTitle: 'Unexpected response',
      badResponseDetail: 'The server answered in a format we do not recognise.',
    },
  },
);

/** Clave tipada de los textos comunes (p. ej. `nav.precios`). */
export type CommonKey = MessageKey<(typeof commonMsgs)['es']>;
